import React, { createContext, useContext, useReducer, useMemo } from 'react';
/* eslint-disable react/jsx-no-constructed-context-values */
import PropTypes from 'prop-types';
import rootReducer from 'reducers';
import useReducerLogger from 'use-reducer-logger';
import storeInitialState from 'store/initial-state';
import useMemoizedSelector from 'hooks/useMemoizedSelector';

export const AppContext = createContext();

export const useEnhancedReducer = (reducer, initialState) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const returnActionDispatch = (action) => {
    dispatch(action);
    return action;
  };

  const getState = () => state;

  const enhancedDispatch = (actionCreator) => {
    if (typeof actionCreator === 'object') {
      return returnActionDispatch(actionCreator);
    }
    return actionCreator(returnActionDispatch, getState);
  };
  return [state, enhancedDispatch];
};

export const wrapReducer = (reducer) => {
  if (process.env.NODE_ENV === 'development') {
    return useReducerLogger(reducer);
  }
  return reducer;
};

export const AppContextProvider = ({ brandId, children }) => {
  const [state, dispatch] = useEnhancedReducer(
    wrapReducer(rootReducer),
    storeInitialState,
  );

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ state, dispatch, brandId }),
    [state, dispatch, brandId],
  );
  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

AppContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  brandId: PropTypes.string.isRequired,
};

export const useAppContext = () => {
  const contextValue = useContext(AppContext);
  return contextValue;
};

export const useBrandId = () => {
  const { brandId } = useContext(AppContext);
  return brandId;
};

export const useAppState = (stateSelector) => {
  const { state } = useContext(AppContext);
  return useMemoizedSelector(state, stateSelector);
};

export const useDispatch = () => {
  const { dispatch } = useContext(AppContext);
  return dispatch;
};
